import { cx } from "@emotion/css";
import { Note } from "@octopusdeploy/design-system-components";
import type { PlatformHubAccountSummary, PropertyValueResource, ScopeValues, TemplateParameterResource } from "@octopusdeploy/octopus-server-client";
import { ControlType, isAccountControlType } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import { useState } from "react";
import type { StepEditorEvent } from "~/analytics/Analytics";
import { useScopeWidth } from "~/areas/platformHub/processEditor/useScopeWidth";
import type { ParameterValueRow, TemplateValueKey } from "~/areas/platformHub/processTemplates/TemplateParametersTable";
import { TemplateParameterScopeCell } from "~/areas/platformHub/processTemplates/TemplateParametersTable";
import { useProcessErrorSelectors } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { getControlTypeFromParameterValueRow, getUniqueAccountVariableNames, templateParameterValueStyles } from "~/areas/projects/components/Process/ProcessTemplates/TemplateParameterHelpers";
import { showMultipleValuesAndScopesIndicators, TemplateParameterValueCell } from "~/areas/projects/components/Process/ProcessTemplates/TemplateParameterValueCell";
import type { StoredAction } from "~/areas/projects/components/Process/types";
import type { ProjectSourceItems, ProjectTemplateSourceItems } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInput";
import ActionTemplateParameterInput from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInput";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import { GroupedDataTable } from "~/components/GroupedDataTable/GroupedDataTable";
import Markdown from "~/components/Markdown/index";
interface ProcessTemplateParametersTableProps {
    parameterValueRows: ParameterValueRow[];
    availableScopeValues: ScopeValues;
    platformHubAccounts: PlatformHubAccountSummary[];
    stepId: string;
    sourceItems: ProjectSourceItems | ProjectTemplateSourceItems;
    action: StoredAction;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    doBusyTask: DoBusyTask;
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
}
export function ProcessTemplateParametersTable({ parameterValueRows, availableScopeValues, platformHubAccounts, stepId, sourceItems, action, onParameterValueChanged, doBusyTask, analyticsStepEditorDispatch }: ProcessTemplateParametersTableProps) {
    const { scopeHeaderRef, scopeWidth } = useScopeWidth();
    const [focusedValueKey, setFocusedValueKey] = useState<TemplateValueKey | null>(null);
    const hideScopeColumn = parameterValueRows.every((row) => !isAccountControlType(getControlTypeFromParameterValueRow(row)));
    return (<GroupedDataTable data={parameterValueRows} columns={[
            {
                title: "Name",
                render: (data) => data.ValueIndex === 0 ? (<>
                                <div className={styles.parameterNameCellPrimaryHeader}>{data.Parameter.Label || data.Parameter.Name}</div>
                                {data.Parameter.Label && <div className={styles.parameterNameCellSecondaryHeader}>{data.Parameter.Name}</div>}
                                <div className={styles.parameterHelpText}>
                                    <Markdown markup={data.Parameter.HelpText} noMargins={true} mutedTextColor={true}/>
                                </div>
                            </>) : null,
                isChild: (data) => data.ValueIndex !== 0,
                collapseNullCellsUpwards: true,
                width: "30%",
                verticalAlign: "top",
            },
            {
                title: "Value",
                render: (data) => (<TemplateParameterValueCell data={data} sourceItems={sourceItems} parameterValues={action.Properties} onParameterValueChanged={onParameterValueChanged} doBusyTask={doBusyTask} analyticsStepEditorDispatch={analyticsStepEditorDispatch} platformHubAccounts={platformHubAccounts} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} renderParameterEditor={({ parameter, autoFocus }) => (<ProcessTemplateParameter key={parameter.Name} stepId={stepId} parameter={parameter} sourceItems={sourceItems} action={action} onParameterValueChanged={onParameterValueChanged} doBusyTask={doBusyTask} analyticsStepEditorDispatch={analyticsStepEditorDispatch} autoFocus={autoFocus} isDefaultValue={!action.Properties[data.Parameter.Name] && !!data.Value}/>)}/>),
                padding: "0",
            },
            ...(hideScopeColumn
                ? []
                : [
                    {
                        title: "Scope",
                        render: (data: ParameterValueRow) => {
                            const controlType = data.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
                            const getScopeCellContents = () => {
                                if (!data.Value) {
                                    return null;
                                }
                                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                                if (getUniqueAccountVariableNames(sourceItems).includes(data.Value.Value as string)) {
                                    return <div className={cx(styles.disabledText, styles.valueContainer)}>Defined by variable</div>;
                                }
                                return (<TemplateParameterScopeCell parameter={data.Parameter} value={data.Value} onChange={noOp} index={data.ValueIndex} isEditing={false} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} scopeValues={availableScopeValues} height={48} width={scopeWidth} isConsumer={true}/>);
                            };
                            return (<>
                                          {showMultipleValuesAndScopesIndicators(controlType, data) && (<div className={styles.multipleValuesAndScopesIndicator}>
                                                  <div>Multiple scopes</div>
                                              </div>)}
                                          <div className={styles.scopeContainer}>{getScopeCellContents()}</div>
                                      </>);
                        },
                        padding: "0",
                        ref: scopeHeaderRef,
                    },
                ]),
        ]} getRowKey={(data) => `${data.Parameter.Name}-${data.ValueIndex}`}/>);
}
interface ProcessTemplateParameterProps {
    parameter: TemplateParameterResource;
    stepId: string;
    sourceItems: ProjectSourceItems | ProjectTemplateSourceItems;
    action: StoredAction;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    doBusyTask: DoBusyTask;
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
    autoFocus: boolean;
    isDefaultValue: boolean;
}
function ProcessTemplateParameter({ stepId, parameter, sourceItems, action, onParameterValueChanged, doBusyTask, analyticsStepEditorDispatch, autoFocus, isDefaultValue }: ProcessTemplateParameterProps) {
    const type = parameter.DisplaySettings["Octopus.ControlType"];
    const value = action.Properties[parameter.Name];
    const processErrorSelectors = useProcessErrorSelectors();
    const getStepFieldError = (parameterName: string) => processErrorSelectors.getStepFieldError(stepId, parameterName);
    return type ? (<div className={styles.parameterContainer}>
            <ActionTemplateParameterInput parameter={parameter} sourceItems={sourceItems} doBusyTask={doBusyTask} value={value} onChange={(value: PropertyValueResource | undefined) => onParameterValueChanged(parameter.Name, value)} actionType={action.ActionType} error={getStepFieldError(parameter.Name)} analyticsStepEditorDispatch={analyticsStepEditorDispatch} autoFocus={autoFocus}/>
            {isDefaultValue && type === ControlType.Sensitive && <Note>Inheriting default value from the parameter.</Note>}
        </div>) : null;
}
const styles = templateParameterValueStyles;
